import cx from "classnames";
import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import type { SponsorPortalSponsor } from "@/modules/sponsorPortal/sponsorPortal.types";
import Services from "@/services/Services";

type SponsorShellProps = {
  sponsor: SponsorPortalSponsor;
  sponsors: SponsorPortalSponsor[];
  children: React.ReactNode;
};

type NavItem = { to: string; labelKey: "overview" | "plans" | "members" | "billing" };

const NAV_ITEMS: NavItem[] = [
  { to: "", labelKey: "overview" },
  { to: "plans", labelKey: "plans" },
  { to: "members", labelKey: "members" },
  { to: "billing", labelKey: "billing" },
];

/**
 * The sponsor portal's frame.
 *
 * Deliberately not wrapped in the patient app's `MobileOnlyLayout`, which pins content to a 390px
 * column from `sm:` upwards. A sponsor administers a benefit from a desk at least as often as from
 * a phone, and a table of members inside a phone-width column on a 27" monitor is unusable. The
 * two layouts are genuinely different rather than one reflowed: a persistent sidebar from `lg:`,
 * and below that a sticky top bar with a slide-over drawer.
 */
export const SponsorShell = ({ sponsor, sponsors, children }: SponsorShellProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const signOut = async () => {
    await Services.auth.clearAll();
    navigate("/sponsor/signin", { replace: true });
  };

  const navLabel = (labelKey: NavItem["labelKey"]) =>
    ({
      overview: t("sponsorPortal.navOverview"),
      plans: t("sponsorPortal.navPlans"),
      members: t("sponsorPortal.navMembers"),
      billing: t("sponsorPortal.navBilling"),
    })[labelKey];

  const navLinks = (onNavigate?: () => void) => (
    <nav className="flex flex-col gap-1" aria-label={t("sponsorPortal.navLabel")}>
      {NAV_ITEMS.map((item) => (
        <NavLink
          key={item.labelKey}
          to={`/sponsor/${sponsor.platformSponsorId}${item.to ? `/${item.to}` : ""}`}
          // `end` only on the index link, so "Overview" is not highlighted while a child route
          // such as /members is active.
          end={item.to === ""}
          onClick={onNavigate}
          className={({ isActive }) =>
            cx(
              "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-brand-color/10 text-brand-color"
                : "text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800",
            )
          }
        >
          {navLabel(item.labelKey)}
        </NavLink>
      ))}
    </nav>
  );

  const sponsorSwitcher =
    sponsors.length > 1 ? (
      <label className="block">
        <span className="sr-only">{t("sponsorPortal.switchSponsor")}</span>
        <select
          value={sponsor.platformSponsorId}
          onChange={(event) => navigate(`/sponsor/${event.target.value}`)}
          className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm font-semibold text-nearly-black dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
        >
          {sponsors.map((option) => (
            <option key={option.platformSponsorId} value={option.platformSponsorId}>
              {option.name}
            </option>
          ))}
        </select>
      </label>
    ) : (
      <p className="truncate text-base font-semibold text-nearly-black dark:text-neutral-100">
        {sponsor.name}
      </p>
    );

  const signOutButton = (
    <button
      type="button"
      onClick={signOut}
      className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
    >
      {t("sponsorPortal.signOut")}
    </button>
  );

  return (
    <div className="min-h-screen bg-background-two dark:bg-neutral-950">
      {/* Mobile top bar — hidden once the sidebar takes over at lg. */}
      <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-neutral-200 bg-white px-4 py-3 dark:border-neutral-800 dark:bg-neutral-900 lg:hidden">
        <button
          type="button"
          onClick={() => setIsDrawerOpen(true)}
          aria-label={t("sponsorPortal.openMenu")}
          aria-expanded={isDrawerOpen}
          className="-ml-2 rounded-lg p-2 text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
          </svg>
        </button>
        <p className="min-w-0 flex-1 truncate text-base font-semibold text-nearly-black dark:text-neutral-100">
          {sponsor.name}
        </p>
      </header>

      {/* Mobile drawer. Rendered only while open so its links stay out of the tab order when shut. */}
      {isDrawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label={t("sponsorPortal.closeMenu")}
            onClick={() => setIsDrawerOpen(false)}
            className="absolute inset-0 bg-black/40"
          />
          <div className="absolute inset-y-0 left-0 flex w-72 max-w-[80%] flex-col gap-6 bg-white p-4 dark:bg-neutral-900">
            {sponsorSwitcher}
            {navLinks(() => setIsDrawerOpen(false))}
            <div className="mt-auto">{signOutButton}</div>
          </div>
        </div>
      )}

      <div className="lg:flex">
        {/* Desktop sidebar. */}
        <aside className="hidden lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-64 lg:shrink-0 lg:flex-col lg:gap-6 lg:border-r lg:border-neutral-200 lg:bg-white lg:p-4 dark:lg:border-neutral-800 dark:lg:bg-neutral-900">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">
              {t("sponsorPortal.sponsorLabel")}
            </p>
            {sponsorSwitcher}
          </div>
          {navLinks()}
          <div className="mt-auto">{signOutButton}</div>
        </aside>

        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-10 lg:py-10">
          <div className="mx-auto w-full max-w-6xl">{children}</div>
        </main>
      </div>
    </div>
  );
};
